import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/lib/hooks/useToast'
import { useUpdatePracticeCase } from '@/api/practice-cases'
import { useHasPermission } from '@/lib/hooks/usePermission'
import type { PracticeCase } from '@/api/practice-cases'

interface EditPracticeCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  practiceCase: PracticeCase
}

export function EditPracticeCaseDialog({
  open,
  onOpenChange,
  practiceCase,
}: EditPracticeCaseDialogProps) {
  const { toast } = useToast()
  const [title, setTitle] = useState(practiceCase.title)
  const [location, setLocation] = useState(practiceCase.location)
  const [errors, setErrors] = useState<string[]>([])

  const { mutateAsync: updatePracticeCase, isPending } = useUpdatePracticeCase()
  const canUpdatePractice = useHasPermission('practice.update')

  // Update form when practice case changes
  useEffect(() => {
    setTitle(practiceCase.title)
    setLocation(practiceCase.location)
    setErrors([])
  }, [practiceCase])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors([])

    // Validation
    if (!title.trim()) {
      setErrors(['Titel is verplicht'])
      return
    }

    if (!location.trim()) {
      setErrors(['Locatie is verplicht'])
      return
    }

    // Check if anything changed
    if (title.trim() === practiceCase.title && location.trim() === practiceCase.location) {
      toast({
        title: 'Geen wijzigingen',
        description: 'Er zijn geen wijzigingen om op te slaan.',
      })
      onOpenChange(false)
      return
    }

    try {
      await updatePracticeCase({
        caseId: practiceCase.id,
        data: {
          title: title.trim(),
          location: location.trim(),
        },
      })

      toast({
        title: 'Oefenscenario bijgewerkt',
        description: `Oefenscenario "${title}" is succesvol bijgewerkt.`,
      })

      onOpenChange(false)
    } catch (error) {
      console.error('Failed to update practice case:', error)
      toast({
        title: 'Fout',
        description: 'Kon oefenscenario niet bijwerken. Probeer het opnieuw.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Oefenscenario bewerken</DialogTitle>
          <DialogDescription>
            Bewerk de titel en locatie van het oefenscenario. Let op: bestanden en gegenereerde
            artikelen kunnen niet worden bewerkt.
          </DialogDescription>
        </DialogHeader>

        {!canUpdatePractice && (
          <div className="mb-4 p-3 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-md dark:bg-orange-950/20 dark:text-orange-400 dark:border-orange-800">
            U heeft geen toestemming om oefenscenario's te bewerken.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="edit-title">
              Titel <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Bijv. Brand in winkelcentrum"
              required
              aria-required="true"
              disabled={!canUpdatePractice}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-location">
              Locatie <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-location"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Bijv. Den Haag, Winkelcentrum De Passage"
              required
              aria-required="true"
              disabled={!canUpdatePractice}
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={isPending || !canUpdatePractice}>
              {isPending ? 'Opslaan...' : 'Wijzigingen opslaan'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
